import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import BottomSheet from './ui/BottomSheet';
import WebScrollView from './WebScrollView';
import { CheckCircle2, ArrowRight } from '../lib/icons';
import { supabase } from '../lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface HomeSystem {
  heating_systems: string[];
  cooling_systems: string[];
  appliances: string[];
  other_systems: string[];
  upgrades: string[];
  outdoor_features: string[];
  maintenance_plan_enabled?: {
    heating_systems?: string[];
    cooling_systems?: string[];
    appliances?: string[];
    other_systems?: string[];
    upgrades?: string[];
    outdoor_features?: string[];
  };
}

type SystemCategory = keyof typeof systemOptions;

interface Props {
  homeId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful save so callers can refresh related data */
  onSaved?: () => void;
}

// ─── System catalog (mirrors MaintenancePlan.tsx) ────────────────────────────

const systemOptions = {
  heating_systems: [
    { id: 'furnace', label: 'Furnace' },
    { id: 'heat_pump', label: 'Heat Pump' },
    { id: 'boiler', label: 'Boiler' },
    { id: 'radiant', label: 'Radiant Heat' },
    { id: 'baseboard', label: 'Electric Baseboard' },
    { id: 'fireplace', label: 'Fireplace' },
  ],
  cooling_systems: [
    { id: 'central_ac', label: 'Central AC' },
    { id: 'mini_split', label: 'Mini Split' },
    { id: 'window_units', label: 'Window Units' },
    { id: 'swamp_cooler', label: 'Swamp Cooler' },
  ],
  appliances: [
    { id: 'washer', label: 'Washer' },
    { id: 'dryer', label: 'Dryer' },
    { id: 'dishwasher', label: 'Dishwasher' },
    { id: 'refrigerator', label: 'Refrigerator' },
    { id: 'oven', label: 'Oven/Range' },
    { id: 'microwave', label: 'Built-in Microwave' },
    { id: 'garbage_disposal', label: 'Garbage Disposal' },
  ],
  other_systems: [
    { id: 'generator', label: 'Generator' },
    { id: 'water_softener', label: 'Water Softener' },
    { id: 'water_filter', label: 'Water Filtration System' },
    { id: 'sump_pump', label: 'Sump Pump' },
    { id: 'security_system', label: 'Security System' },
  ],
  upgrades: [
    { id: 'solar_panels', label: 'Solar Panels' },
    { id: 'ev_charger', label: 'EV Charger' },
    { id: 'electronic_locks', label: 'Electronic Locks' },
    { id: 'smart_thermostat', label: 'Smart Thermostat' },
    { id: 'whole_house_fan', label: 'Whole House Fan' },
  ],
  outdoor_features: [
    { id: 'gutters', label: 'Gutters' },
    { id: 'pool', label: 'Pool' },
    { id: 'hot_tub', label: 'Hot Tub' },
    { id: 'sprinklers', label: 'Sprinkler System' },
    { id: 'deck', label: 'Deck' },
    { id: 'patio', label: 'Patio' },
    { id: 'fence', label: 'Fence' },
  ],
};

const WIZARD_STEPS = [
  { type: 'heating_systems' as SystemCategory, title: 'Heating', description: 'How is your home heated?' },
  { type: 'cooling_systems' as SystemCategory, title: 'Cooling', description: 'How is your home cooled?' },
  { type: 'appliances' as SystemCategory, title: 'Appliances', description: 'Which major appliances do you have?' },
  { type: 'other_systems' as SystemCategory, title: 'Other Systems', description: 'Do you have any of these systems?' },
  { type: 'upgrades' as SystemCategory, title: 'Upgrades', description: 'Have you installed any of these upgrades?' },
  { type: 'outdoor_features' as SystemCategory, title: 'Outdoor Features', description: 'What features do you have outside?' },
];

const CATEGORY_LABELS: Record<SystemCategory, string> = {
  heating_systems: 'Heating Systems',
  cooling_systems: 'Cooling Systems',
  appliances: 'Appliances',
  other_systems: 'Other Systems',
  upgrades: 'Upgrades',
  outdoor_features: 'Outdoor Features',
};

const REVIEW_STEP_INDEX = WIZARD_STEPS.length;

const emptySystemData = (): HomeSystem => ({
  heating_systems: [],
  cooling_systems: [],
  appliances: [],
  other_systems: [],
  upgrades: [],
  outdoor_features: [],
});

// ─── Component ────────────────────────────────────────────────────────────────

const ConfigureHomeSystemsModal: React.FC<Props> = ({ homeId, isOpen, onClose, onSaved }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [systemData, setSystemData] = useState<HomeSystem>(emptySystemData());
  const [maintenancePlanEnabled, setMaintenancePlanEnabled] = useState<Required<HomeSystem['maintenance_plan_enabled']>>({
    heating_systems: [],
    cooling_systems: [],
    appliances: [],
    other_systems: [],
    upgrades: [],
    outdoor_features: [],
  });
  const [loadingData, setLoadingData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const reviewStepInitialized = useRef(false);

  // Load existing home systems whenever the modal opens
  useEffect(() => {
    if (isOpen && homeId) {
      setCurrentStep(0);
      setError('');
      reviewStepInitialized.current = false;
      void fetchHomeSystem();
    }
  }, [isOpen, homeId]);

  // Auto-enable all configured systems when first landing on the review step
  useEffect(() => {
    if (currentStep === REVIEW_STEP_INDEX && !reviewStepInitialized.current) {
      const hasAnySystems = Object.values(systemData).some(
        (v) => Array.isArray(v) && v.length > 0
      );
      const allEmpty = Object.values(maintenancePlanEnabled).every((arr) => arr.length === 0);
      if (hasAnySystems && allEmpty) {
        setMaintenancePlanEnabled({
          heating_systems: systemData.heating_systems,
          cooling_systems: systemData.cooling_systems,
          appliances: systemData.appliances,
          other_systems: systemData.other_systems,
          upgrades: systemData.upgrades,
          outdoor_features: systemData.outdoor_features,
        });
      }
      reviewStepInitialized.current = true;
    } else if (currentStep !== REVIEW_STEP_INDEX) {
      reviewStepInitialized.current = false;
    }
  }, [currentStep, systemData, maintenancePlanEnabled]);

  const fetchHomeSystem = async () => {
    try {
      setLoadingData(true);
      const { data, error: fetchError } = await supabase
        .from('home_systems')
        .select('*')
        .eq('home_id', homeId);

      if (fetchError) throw fetchError;

      if (data && data.length > 0) {
        const system = data[0];
        setSystemData({
          heating_systems: system.heating_systems || [],
          cooling_systems: system.cooling_systems || [],
          appliances: system.appliances || [],
          other_systems: system.other_systems || [],
          upgrades: system.upgrades || [],
          outdoor_features: system.outdoor_features || [],
        });
        const enabled = system.maintenance_plan_enabled || {};
        setMaintenancePlanEnabled({
          heating_systems: enabled.heating_systems || [],
          cooling_systems: enabled.cooling_systems || [],
          appliances: enabled.appliances || [],
          other_systems: enabled.other_systems || [],
          upgrades: enabled.upgrades || [],
          outdoor_features: enabled.outdoor_features || [],
        });
      } else {
        setSystemData(emptySystemData());
        setMaintenancePlanEnabled({
          heating_systems: [],
          cooling_systems: [],
          appliances: [],
          other_systems: [],
          upgrades: [],
          outdoor_features: [],
        });
      }
    } catch (err) {
      console.error('Error fetching home system:', err);
      setSystemData(emptySystemData());
    } finally {
      setLoadingData(false);
    }
  };

  const handleSystemToggle = (category: SystemCategory, id: string) => {
    setSystemData((prev) => ({
      ...prev,
      [category]: prev[category].includes(id)
        ? prev[category].filter((item) => item !== id)
        : [...prev[category], id],
    }));
  };

  const handleMaintenancePlanToggle = (category: SystemCategory, systemId: string) => {
    setMaintenancePlanEnabled((prev) => {
      const current = prev[category] || [];
      return {
        ...prev,
        [category]: current.includes(systemId)
          ? current.filter((id) => id !== systemId)
          : [...current, systemId],
      };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const { error: upsertError } = await supabase
        .from('home_systems')
        .upsert(
          { home_id: homeId, ...systemData, maintenance_plan_enabled: maintenancePlanEnabled },
          { onConflict: 'home_id' }
        );
      if (upsertError) throw upsertError;

      const { error: generateError } = await supabase.rpc('generate_maintenance_plans_for_home', {
        home_id_param: homeId,
      });
      if (generateError) {
        console.error('Error generating maintenance plans:', generateError);
      }

      onSaved?.();
      onClose();
    } catch (err: any) {
      console.error('Error saving home systems:', err);
      setError('Failed to save home systems. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderSetupStep = () => {
    const step = WIZARD_STEPS[currentStep];
    const options = systemOptions[step.type];
    const selectedItems = systemData[step.type];

    return (
      <View style={styles.stepContainer}>
        <View style={styles.stepHeader}>
          <Text style={styles.stepTitle}>{step.title}</Text>
          <Text style={styles.stepDescription}>{step.description}</Text>
        </View>
        <View style={styles.optionsGrid}>
          {options.map((option) => {
            const selected = selectedItems.includes(option.id);
            return (
              <Pressable
                key={option.id}
                onPress={() => handleSystemToggle(step.type, option.id)}
                style={[styles.option, selected && styles.optionSelected]}
              >
                <Text style={[styles.optionText, selected && styles.optionTextSelected]}>
                  {option.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>
    );
  };

  const renderReviewStep = () => {
    const hasAnySystems = Object.values(systemData).some(
      (v) => Array.isArray(v) && v.length > 0
    );

    if (!hasAnySystems) {
      return (
        <View style={styles.stepContainer}>
          <Text style={styles.stepTitle}>Review Configuration</Text>
          <Text style={styles.stepDescription}>
            No systems configured yet. Go back to add systems to this home.
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.stepContainer}>
        <View style={styles.stepHeader}>
          <Text style={styles.stepTitle}>Review & Enable Maintenance Plans</Text>
          <Text style={styles.stepDescription}>
            Toggle which systems should have maintenance plans generated.
          </Text>
        </View>
        {(Object.keys(systemOptions) as SystemCategory[]).map((category) => {
          const configured = systemData[category];
          if (!configured || configured.length === 0) return null;
          return (
            <View key={category} style={styles.reviewCategory}>
              <Text style={styles.reviewCategoryTitle}>{CATEGORY_LABELS[category]}</Text>
              {configured.map((systemId) => {
                const label =
                  systemOptions[category].find((o) => o.id === systemId)?.label ?? systemId;
                const isEnabled = (maintenancePlanEnabled[category] || []).includes(systemId);
                return (
                  <View key={systemId} style={styles.reviewRow}>
                    <Text style={styles.reviewLabel}>{label}</Text>
                    <Pressable
                      onPress={() => handleMaintenancePlanToggle(category, systemId)}
                      style={[styles.toggle, isEnabled && styles.toggleEnabled]}
                    >
                      <View style={[styles.toggleThumb, isEnabled && styles.toggleThumbEnabled]} />
                    </Pressable>
                  </View>
                );
              })}
            </View>
          );
        })}
      </View>
    );
  };

  const isReviewStep = currentStep === REVIEW_STEP_INDEX;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Configure Home Systems"
      height="full"
    >
      <View style={styles.container}>
        {/* Progress bar */}
        <View style={styles.progressBarRow}>
          {[...WIZARD_STEPS, { type: 'review' }].map((_, i) => (
            <View
              key={i}
              style={[styles.progressSegment, i <= currentStep && styles.progressSegmentActive]}
            />
          ))}
        </View>

        {/* Step content */}
        <WebScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
          {loadingData ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#9ca3af" />
            </View>
          ) : isReviewStep ? (
            renderReviewStep()
          ) : (
            renderSetupStep()
          )}

          {error ? (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : null}
        </WebScrollView>

        {/* Navigation footer */}
        <View style={styles.footer}>
          <Pressable
            onPress={() => setCurrentStep((s) => s - 1)}
            disabled={currentStep === 0}
            style={[styles.backBtn, currentStep === 0 && styles.backBtnDisabled]}
          >
            <Text style={[styles.backBtnText, currentStep === 0 && styles.backBtnTextDisabled]}>
              Back
            </Text>
          </Pressable>

          <Pressable
            onPress={() => {
              if (isReviewStep) {
                void handleSubmit();
              } else {
                setCurrentStep((s) => s + 1);
              }
            }}
            disabled={submitting}
            style={[styles.nextBtn, submitting && styles.nextBtnDisabled]}
          >
            {submitting ? (
              <>
                <ActivityIndicator size="small" color="#ffffff" />
                <Text style={styles.nextBtnText}>Saving...</Text>
              </>
            ) : isReviewStep ? (
              <>
                <Text style={styles.nextBtnText}>Finish</Text>
                <CheckCircle2 size={16} color="#ffffff" />
              </>
            ) : (
              <>
                <Text style={styles.nextBtnText}>Next</Text>
                <ArrowRight size={16} color="#ffffff" />
              </>
            )}
          </Pressable>
        </View>
      </View>
    </BottomSheet>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },
  progressBarRow: {
    flexDirection: 'row',
    gap: 4,
    paddingBottom: 12,
  },
  progressSegment: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#e5e7eb',
  },
  progressSegmentActive: {
    backgroundColor: '#111827',
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    paddingBottom: 16,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  stepContainer: {
    gap: 16,
  },
  stepHeader: {
    gap: 4,
  },
  stepTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  stepDescription: {
    fontSize: 14,
    color: '#6b7280',
  },
  optionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 8,
  },
  option: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: '#e5e7eb',
    backgroundColor: '#f9fafb',
    minHeight: 48,
    justifyContent: 'center',
  },
  optionSelected: {
    borderColor: '#111827',
    backgroundColor: '#111827',
  },
  optionText: {
    fontSize: 15,
    color: '#374151',
    fontWeight: '500',
  },
  optionTextSelected: {
    color: '#ffffff',
  },
  reviewCategory: {
    gap: 8,
    paddingTop: 4,
  },
  reviewCategoryTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  reviewRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  reviewLabel: {
    fontSize: 15,
    color: '#111827',
    flex: 1,
  },
  toggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#d1d5db',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  toggleEnabled: {
    backgroundColor: '#111827',
  },
  toggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#ffffff',
  },
  toggleThumbEnabled: {
    alignSelf: 'flex-end',
  },
  errorContainer: {
    marginTop: 12,
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
  },
  errorText: {
    fontSize: 14,
    color: '#dc2626',
  },
  footer: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  backBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: '#f3f4f6',
    alignItems: 'center',
  },
  backBtnDisabled: {
    opacity: 0.4,
  },
  backBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#374151',
  },
  backBtnTextDisabled: {
    color: '#9ca3af',
  },
  nextBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: '#111827',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  nextBtnDisabled: {
    opacity: 0.6,
  },
  nextBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
});

export default ConfigureHomeSystemsModal;
